import { IconBadge } from '@trellis/roots/media'
import type { IconBadgeProps } from '@trellis/roots/media'
import type { ReminderTimelineMark } from '@trellis/shared'
import { Bell, Mail } from 'lucide-react'

import { cn } from '@/lib/utils'

export type ReminderMarkDotKind = 'email' | 'alert' | 'emailAndAlert' | 'repeat'

export const reminderMarkDotKind = (
  mark: Pick<ReminderTimelineMark, 'emailClient' | 'alertStaff' | 'isRepeat'>,
): ReminderMarkDotKind =>
  mark.isRepeat
    ? 'repeat'
    : mark.emailClient && mark.alertStaff
      ? 'emailAndAlert'
      : mark.emailClient
        ? 'email'
        : 'alert'

const KIND_PROPS: Record<
  ReminderMarkDotKind,
  Pick<IconBadgeProps, 'icon' | 'tone' | 'variant' | 'cornerBadge'>
> = {
  email: { icon: Mail, tone: 'sky', variant: 'solid' },
  emailAndAlert: {
    icon: Mail,
    tone: 'sky',
    variant: 'solid',
    cornerBadge: { icon: Bell, tone: 'amber' },
  },
  alert: { icon: Bell, tone: 'amber', variant: 'solid' },
  repeat: { icon: Mail, tone: 'sky', variant: 'outline' },
}

/** {@link IconBadge} for a reminder step: mail, bell, both, or a hollow repeat. */
export function ReminderMarkDot({
  kind,
  size = 'md',
  className,
}: {
  kind: ReminderMarkDotKind
  size?: 'sm' | 'md'
  className?: string
}) {
  const kindProps = KIND_PROPS[kind]
  return (
    <IconBadge
      {...kindProps}
      size={size === 'sm' ? 'xs' : 'md'}
      className={cn(
        // A background-coloured rim keeps solid dots distinct where they cross the track.
        kindProps.variant === 'solid' &&
          (size === 'sm'
            ? 'border-background border-[1.5px]'
            : 'border-background border-2'),
        className,
      )}
    />
  )
}
